import { Suspense, useCallback, useState } from "react";
import { DeleteConfirmationDialog } from "@/shared/components/DeleteConfirmationDialog";
import BaseDrawer from "@/shared/components/Drawer";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/shared/ui/Tabs";
import { lazyWithRetry } from "@/shared/utils/lazyWithRetry";
import { useBankMovementDetailQuery } from "../hooks/useAccountingQueries";
import AllocationsList from "./movement-allocations/AllocationsList";
import CategoryManagerDrawer from "./movement-allocations/CategoryManagerDrawer";
import EcommerceAllocationDetail from "./movement-allocations/EcommerceAllocationDetail";
import EcommerceSearchDrawer from "./movement-allocations/EcommerceSearchDrawer";
import RefundSearchDrawer from "./movement-allocations/RefundSearchDrawer";
import ReimbursementAllocationDetail from "./movement-allocations/ReimbursementAllocationDetail";
import RulesManagerDrawer from "./movement-allocations/RulesManagerDrawer";
import SimpleAllocationEditor from "./movement-allocations/SimpleAllocationEditor";
import StockAllocationEditor from "./movement-allocations/StockAllocationEditor";
import SettlementEditor from "./settlement-allocations/SettlementEditor";
import { useSettlementDrawerState } from "./settlement-allocations/useSettlementDrawerState";
import type { Settlement } from "../types/accounting.types";

const MovementAllocationsDrawer = lazyWithRetry(() => import("./MovementAllocationsDrawer"));

type SettlementAllocationsDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  settlement: Settlement | null
  onSettlementUpdated?: (settlement: Settlement) => void
  onSettlementDeleted?: () => void
  zIndex?: number
  highlightedAllocationId?: string | null
};

const SettlementAllocationsDrawer = ({
  open,
  onOpenChange,
  settlement,
  onSettlementUpdated,
  onSettlementDeleted,
  zIndex,
  highlightedAllocationId,
}: SettlementAllocationsDrawerProps) => {
  const state = useSettlementDrawerState({
    open,
    onOpenChange,
    settlement,
    onSettlementUpdated,
    onSettlementDeleted,
  });

  // Nested movement drawer
  const [movementDrawerId, setMovementDrawerId] = useState<string | null>(null);
  const [movementDrawerOpen, setMovementDrawerOpen] = useState(false);
  const { data: movementData } = useBankMovementDetailQuery(movementDrawerId, Boolean(movementDrawerId));
  const handleOpenMovement = useCallback((id: string) => {
    setMovementDrawerId(id);
    setMovementDrawerOpen(true);
  }, []);

  return (
    <>
      <BaseDrawer
        drawerId="DRW-STL"
        open={open}
        onOpenChange={state.handleDrawerOpenChange}
        width="893px"
        zIndex={zIndex}
        isFirstLevel={!zIndex}
        onBack={zIndex ? () => onOpenChange(false) : undefined}
        bodyClassName="[&_*]:select-text"
        title={state.settlementId
          ? (
            <>
              Règlement
              {" "}
              <span className="text-base font-normal text-slate-400">
                #
                {state.settlementId}
              </span>
            </>
          )
          : "Règlement"}
        description="Éditez le règlement et consultez ses lignes d'affectation du grand livre."
      >
        <SettlementEditor
          settlement={settlement}
          settlementDraft={state.settlementDraft}
          setSettlementField={state.setSettlementField}
          isSettlementBusy={state.isSettlementBusy}
          canSaveSettlement={state.canSaveSettlement}
          accountPopoverOpen={state.accountPopoverOpen}
          setAccountPopoverOpen={state.setAccountPopoverOpen}
          accountSearch={state.accountSearch}
          setAccountSearch={state.setAccountSearch}
          bankAccounts={state.bankAccounts}
          isBankAccountsLoading={state.isBankAccountsLoading}
          selectedAccount={state.selectedAccount}
          paymentMethodPopoverOpen={state.paymentMethodPopoverOpen}
          setPaymentMethodPopoverOpen={state.setPaymentMethodPopoverOpen}
          paymentMethodSearch={state.paymentMethodSearch}
          setPaymentMethodSearch={state.setPaymentMethodSearch}
          filteredPaymentMethods={state.filteredPaymentMethods}
          isPaymentMethodsLoading={state.isPaymentMethodsLoading}
          onSaveSettlement={state.handleSaveSettlement}
          onDeleteSettlement={() => state.setIsDeleteDialogOpen(true)}
          isDeleteBusy={state.isDeleteBusy}
          linkedMovement={state.linkedMovement}
          isLoadingMovement={state.isLoadingLinkedMovement}
          movementSearchQuery={state.movementSearchQuery}
          onMovementSearchChange={state.handleMovementSearchChange}
          movementSearchResults={state.movementSearchResults}
          isSearchingMovement={state.isSearchingMovement}
          attachingMovementId={state.attachingMovementId}
          isDetachingMovement={state.isDetachingMovement}
          onAttachMovement={state.handleAttachMovement}
          onDetachMovement={state.handleDetachMovement}
          onOpenMovement={handleOpenMovement}
          isBusy={state.isBusy}
          isCreatePending={state.isCreatePending}
          onAddAllocation={state.handleAdd}
          onOpenRefundSearch={() => state.setRefundSearchOpen(true)}
          onOpenEcommerceSearch={() => state.setEcommerceSearchOpen(true)}
        />

        <Tabs defaultValue="ecritures">
          <TabsList className="w-full grid grid-cols-1">
            <TabsTrigger value="ecritures" className="text-[12px]">
              Écritures
              <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-700">
                {state.allocations.length}
              </span>
            </TabsTrigger>
          </TabsList>
          <TabsContent value="ecritures">
            <AllocationsList
              allocations={state.allocations}
              isLoading={state.isLoading}
              getState={state.getState}
              setField={state.setField}
              isAllocationDirty={state.isAllocationDirty}
              onSave={state.handleSave}
              onDelete={state.deleteAllocation}
              isBusy={state.isBusy}
              operationPopoverAllocationId={state.operationPopoverAllocationId}
              setOperationPopoverAllocationId={state.setOperationPopoverAllocationId}
              operationPopoverSearchByAllocationId={state.operationPopoverSearchByAllocationId}
              setOperationPopoverSearchByAllocationId={state.setOperationPopoverSearchByAllocationId}
              operationSearchBusyByAllocationId={state.operationSearchBusyByAllocationId}
              markOperationSearchBusy={state.markOperationSearchBusy}
              operationTypesByBudget={state.operationTypesByBudget}
              onFindOrCreateOperationType={state.handleFindOrCreateOperationTypeForAllocation}
              isCreateOperationTypePending={state.isCreateOperationTypePending}
              onOpenSimpleEditor={(allocationId) => state.setSimpleEditorAllocationId(allocationId)}
              onOpenReimbursementEditor={(allocationId) => state.setReimbursementEditorAllocationId(allocationId)}
              onOpenEcommerceEditor={(allocationId) => state.setEcommerceEditorAllocationId(allocationId)}
              onOpenLegacyEditor={() => {}}
              onOpenStockEditor={(allocationId) => state.setStockEditorAllocationId(allocationId)}
              isCreatePending={state.isCreatePending}
              deletingAllocationId={state.deletingAllocationId}
              totalDebit={state.totalDebit}
              totalCredit={state.totalCredit}
              totalToAllocate={state.totalToAllocate}
              remainingToAllocate={state.remainingToAllocate}
              highlightedAllocationId={highlightedAllocationId}
            />
          </TabsContent>
        </Tabs>
      </BaseDrawer>

      <DeleteConfirmationDialog
        isOpen={state.isDeleteDialogOpen}
        onClose={() => {
          if (state.isDeleteBusy) return;
          state.setIsDeleteDialogOpen(false);
        }}
        onConfirm={state.handleDeleteSettlement}
        isLoading={state.isDeleteBusy}
        itemName={state.settlementId || "ce règlement"}
        title="Confirmer la suppression du règlement"
        description="Ce règlement sera supprimé ainsi que les écritures du grand livre qui lui sont liées. Cette action est irréversible."
        confirmLabel={state.isDeleteBusy ? "Suppression..." : "Supprimer le règlement"}
      />

      <SimpleAllocationEditor
        open={Boolean(state.simpleEditorAllocationId)}
        onClose={state.closeSimpleEditor}
        selectedAllocation={state.selectedSimpleAllocation}
        simpleEditorRow={state.simpleEditorRow}
        setField={state.setField}
        isSimpleEditorBusy={state.isSimpleEditorBusy}
        isLoadingOperationTypes={state.isLoadingOperationTypes}
        isCreateOperationTypePending={state.isCreateOperationTypePending}
        operationTypeSearch={state.operationTypeSearch}
        setOperationTypeSearch={state.setOperationTypeSearch}
        simpleCategoryPopoverOpen={state.simpleCategoryPopoverOpen}
        setSimpleCategoryPopoverOpen={state.setSimpleCategoryPopoverOpen}
        simpleCategoryPopoverSearch={state.simpleCategoryPopoverSearch}
        setSimpleCategoryPopoverSearch={state.setSimpleCategoryPopoverSearch}
        markSimpleCategorySearchBusy={state.markSimpleCategorySearchBusy}
        simpleCategoryContainerRef={state.simpleCategoryContainerRef}
        operationTypes={state.operationTypes}
        supplierSearch={state.simpleEditorSupplierSearch}
        setSupplierSearch={state.setSimpleEditorSupplierSearch}
        suppliers={state.simpleEditorSuppliers}
        isLoadingSuppliers={state.isLoadingSimpleEditorSuppliers}
        selectedSupplierId={state.simpleEditorSelectedSupplierId}
        onSelectSupplier={(id, name) => {
          state.setSimpleEditorSelectedSupplierId(id);
          state.setSimpleEditorSupplierSearch(name);
          state.setIsSimpleEditorSupplierPopoverOpen(false);
        }}
        onClearSupplier={() => {
          state.setSimpleEditorSelectedSupplierId("");
          state.setSimpleEditorSupplierSearch("");
        }}
        isSupplierPopoverOpen={state.isSimpleEditorSupplierPopoverOpen}
        setIsSupplierPopoverOpen={state.setIsSimpleEditorSupplierPopoverOpen}
        supplierContainerRef={state.simpleEditorSupplierContainerRef}
        onSave={state.handleSaveSimpleEditor}
        onFindOrCreateOperationType={state.handleFindOrCreateOperationType}
        onOpenCategoryManager={() => state.setIsCategoryManagerOpen(true)}
        onOpenRulesManager={state.openRulesManager}
      />

      <ReimbursementAllocationDetail
        open={Boolean(state.reimbursementEditorAllocationId)}
        onClose={state.closeReimbursementEditor}
        selectedAllocation={state.selectedReimbursementAllocation}
        simpleEditorRow={state.reimbursementEditorRow}
        setField={state.setField}
        isSimpleEditorBusy={state.isSimpleEditorBusy}
        onSave={state.handleSaveReimbursementEditor}
      />

      <EcommerceAllocationDetail
        open={Boolean(state.ecommerceEditorAllocationId)}
        onClose={state.closeEcommerceEditor}
        selectedAllocation={state.selectedEcommerceAllocation}
        simpleEditorRow={state.ecommerceEditorRow}
        setField={state.setField}
        isSimpleEditorBusy={state.isSimpleEditorBusy}
        onSave={state.handleSaveEcommerceEditor}
      />

      <StockAllocationEditor
        open={Boolean(state.stockEditorAllocationId)}
        onClose={state.closeStockEditor}
        bankMovementId={state.bankMovementId}
        allocation={state.selectedStockAllocation}
        isBusy={state.isBusy}
        onSave={state.handleSaveStockEditor}
      />

      <RefundSearchDrawer
        open={state.refundSearchOpen}
        onClose={() => state.setRefundSearchOpen(false)}
        isBusy={state.isBusy}
        onSelectOperation={state.handleAddRefund}
      />

      <EcommerceSearchDrawer
        open={state.ecommerceSearchOpen}
        onClose={() => state.setEcommerceSearchOpen(false)}
        isBusy={state.isBusy}
        onSelectOperation={state.handleAddEcommerceOperation}
      />

      <RulesManagerDrawer
        open={state.isRulesManagerOpen}
        onOpenChange={state.setIsRulesManagerOpen}
        ruleName={state.ruleName}
        setRuleName={state.setRuleName}
        ruleConditions={state.ruleConditions}
        updateRuleCondition={state.updateRuleCondition}
        addRuleCondition={state.addRuleCondition}
        removeRuleCondition={state.removeRuleCondition}
        ruleBudgetType={state.ruleBudgetType}
        setRuleBudgetType={state.setRuleBudgetType}
        ruleOperationTypeId={state.ruleOperationTypeId}
        setRuleOperationTypeId={state.setRuleOperationTypeId}
        ascOperationTypes={state.ascOperationTypes}
        aepOperationTypes={state.aepOperationTypes}
        bankAccounts={state.bankAccounts}
        isBankAccountsLoading={state.isBankAccountsLoading}
        ruleActionInProgress={state.ruleActionInProgress}
        onCreateRule={state.handleCreateLocalRule}
        onToggleRuleActive={state.handleToggleLocalRuleActive}
        onDeleteRule={state.handleDeleteLocalRule}
        sortedLocalRules={state.sortedLocalRules}
        isCategorizationRulesLoading={state.isCategorizationRulesLoading}
      />

      <CategoryManagerDrawer
        open={state.isCategoryManagerOpen}
        onOpenChange={state.setIsCategoryManagerOpen}
        bankMovementId={state.bankMovementId}
        selectedSimpleBudgetType={state.selectedSimpleBudgetType}
        categoryManagerFilter={state.categoryManagerFilter}
        setCategoryManagerFilter={state.setCategoryManagerFilter}
        operationTypes={state.operationTypes}
        inactiveOperationTypes={state.inactiveOperationTypes}
        isLoadingOperationTypes={state.isLoadingOperationTypes}
        isLoadingInactiveOperationTypes={state.isLoadingInactiveOperationTypes}
        isSimpleEditorBusy={state.isSimpleEditorBusy}
        onDisableOperationType={state.handleDisableOperationType}
        onEnableOperationType={state.handleEnableOperationType}
        onRenameOperationType={state.handleRenameOperationType}
        onUpdateAccountNumber={state.handleUpdateAccountNumber}
        onMergeOperationType={state.handleMergeOperationType}
        onDeleteOperationType={state.handleDeleteOperationType}
        onOperationTypeRemoved={state.onOperationTypeRemoved}
      />

      {movementDrawerOpen && (
        <Suspense fallback={null}>
          <MovementAllocationsDrawer
            open={movementDrawerOpen}
            onOpenChange={setMovementDrawerOpen}
            bankMovement={movementData ?? null}
          />
        </Suspense>
      )}
    </>
  );
};

export default SettlementAllocationsDrawer;
